class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    const overlay = this.querySelector('#CartDrawer-Overlay');
    if (overlay) overlay.addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.syncEmptyState();

    this.glxBylRefreshTimer = null;
    this.glxBylRefreshController = null;
    this.glxBylSyncHandler = () => {
      if (this.glxBylRefreshTimer) window.clearTimeout(this.glxBylRefreshTimer);
      this.glxBylRefreshTimer = window.setTimeout(() => {
        this.glxBylRefreshTimer = null;
        this.refreshAfterBylSync();
      }, 0);
    };
    document.addEventListener('glx:byl:cart-synced', this.glxBylSyncHandler);
  }

  disconnectedCallback() {
    if (this.glxBylRefreshTimer) window.clearTimeout(this.glxBylRefreshTimer);
    this.glxBylRefreshController?.abort();
    if (this.glxBylSyncHandler) document.removeEventListener('glx:byl:cart-synced', this.glxBylSyncHandler);
  }

  syncEmptyState() {
    const hasItems = Boolean(this.querySelector('.cart-item'));
    const hasEmptyPanel = Boolean(this.querySelector('.drawer__inner-empty'));

    if (hasItems) {
      this.classList.remove('is-empty');
    } else if (hasEmptyPanel) {
      this.classList.add('is-empty');
    }
  }

  async refreshAfterBylSync() {
    this.glxBylRefreshController?.abort();
    this.glxBylRefreshController = new AbortController();
    const signal = this.glxBylRefreshController.signal;

    try {
      const sectionIds = this.getSectionsToRender().map((section) => section.id).filter(Boolean);
      if (!sectionIds.length) return;

      const url = new URL(window.location.href);
      url.searchParams.set('sections', sectionIds.join(','));

      const response = await fetch(url.toString(), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
        signal,
      });
      if (!response.ok) throw new Error(`Fresh cart render failed with ${response.status}`);

      const sections = await response.json();
      if (signal.aborted || !sections || typeof sections !== 'object') return;

      this.renderContents({ sections });
    } catch (error) {
      if (error?.name !== 'AbortError') {
        console.warn('[Glowinex cart] Post-BYL cart refresh failed.', error);
      }
    }
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink || cartLink.dataset.glxDrawerBound === 'true') return;

    cartLink.dataset.glxDrawerBound = 'true';
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.syncEmptyState();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    if (!cartDrawerNote) return;
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    const controlledElement = cartDrawerNote.nextElementSibling;
    if (controlledElement?.id) cartDrawerNote.setAttribute('aria-controls', controlledElement.id);

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details')?.hasAttribute('open'));
    });

    cartDrawerNote.parentElement?.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState || typeof parsedState !== 'object') return;

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const sectionHtml = parsedState.sections?.[section.id];
      if (!sectionElement || typeof sectionHtml !== 'string') return;

      const nextHtml = this.getSectionInnerHTML(sectionHtml, section.selector);
      if (nextHtml === null) return;
      sectionElement.innerHTML = nextHtml;
    });

    this.syncEmptyState();

    setTimeout(() => {
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay && overlay.dataset.glxCloseBound !== 'true') {
        overlay.dataset.glxCloseBound = 'true';
        overlay.addEventListener('click', this.close.bind(this));
      }
      this.setHeaderCartIconAccessibility();
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const target = parsed.querySelector(selector);
    return target ? target.innerHTML : null;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);
